/**
 * One source's health line and its declared capabilities, shared by the
 * Clients page and the Integrations pages (task 69).
 */

import { Link } from '@tanstack/react-router';

import type { CapabilityManifest, SourceInfo } from '@bluesphere/api-client';

import { sourceLabel, timeAgo } from '@bluesphere/mdr-alert-panel';
import { HealthChip, SourceBadge } from './states';

/** "synced 4m ago · 12 in 24h", "never synced", or "removed". */
export function syncSummary(info: SourceInfo): string {
  if (info.state === 'removed') return 'removed';
  if (info.last_success_at) {
    return `synced ${timeAgo(info.last_success_at)} · ${info.detections_24h ?? 0} in 24h`;
  }
  return 'never synced';
}

export function SourceLine({
  name,
  info,
  linkToIntegration = false,
  showCapabilities = true,
}: {
  name: string;
  info: SourceInfo;
  /** Make the source name a link to its Integrations page. */
  linkToIntegration?: boolean;
  showCapabilities?: boolean;
}) {
  const removed = info.state === 'removed';
  const label = <span className="text-xs font-medium text-bluesphere-textOnLight">{sourceLabel(name)}</span>;
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-2">
        {linkToIntegration ? (
          <Link
            to="/integrations/$name"
            params={{ name }}
            className="hover:underline"
            title={`Manage ${sourceLabel(name)}`}
          >
            {label}
          </Link>
        ) : (
          label
        )}
        <HealthChip status={removed ? 'pending' : (info.health ?? 'pending')} />
        <span className="text-xs text-bluesphere-muted" title={info.last_success_at ?? undefined}>
          {syncSummary(info)}
        </span>
        {!removed && info.last_error && info.health !== 'healthy' && (
          <span className="max-w-xs truncate text-xs text-red-700" title={info.last_error}>
            {info.last_error}
          </span>
        )}
      </div>
      {showCapabilities && !removed && info.capabilities && (
        <CapabilitiesLine manifest={info.capabilities} />
      )}
    </div>
  );
}

/**
 * What the connector declares it could do (Story 18). Which of it the
 * platform executes, and for which Client, is the response layer's
 * decision (Story 19): a Client's response settings and consent, then the
 * rails on every request. The sentence says "declares", not "can".
 */
export function CapabilitiesLine({ manifest }: { manifest: CapabilityManifest }) {
  const declared = Object.entries(manifest.actions).flatMap(([asset, names]) =>
    names.map((n) => `${n.replaceAll('_', ' ')} (${asset})`),
  );
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-bluesphere-muted">
      <SourceBadge label={`${manifest.role} source`} />
      <SourceBadge label={manifest.maturity === 'ga' ? 'GA' : 'Beta'} />
      <span title="Declared by the connector. Executed only through the response layer, per Client settings and under the safety rails.">
        {declared.length > 0
          ? `declares: ${declared.join(', ')}`
          : 'no response actions declared'}
      </span>
    </div>
  );
}
